/**
 * Daily Loan (DL) calculations.
 *
 * WHY THIS FILE EXISTS SEPARATELY FROM utils.js
 *
 * DL collects once a day for 100 days. Every other product collects monthly,
 * and utils.js is built on that assumption: the schedule generator walks
 * `monthOffset++`, and the interest charge divides the annual rate by 12 in
 * eight different places. Run a daily loan through that code and it is charged
 * a full month of interest for every single day.
 *
 * The fix is NOT to make those shared functions frequency-aware. utils.js holds
 * three near-identical 200-line calculation bodies (BL, GPL, JLG) that already
 * differ in how they measure DPD, and editing them puts three live products at
 * risk for no DL benefit. So DL keeps its own arithmetic here, and utils.js is
 * left alone.
 *
 * See .claude/skills/daily-loan/SKILL.md for the full product rules.
 */

import {
  getDisbursementAmount,
  isValidPaidReceiptWithApproval,
  formatAmount,
} from "./utils";

/**
 * The default number of daily collections.
 *
 * Four terms are offered - see DL_TENURES below. This is only the fallback used
 * when a caller does not name one; the tenure is a parameter everywhere rather
 * than a literal, so no code has to change if the list changes.
 */
export const DL_DEFAULT_INSTALMENTS = 100;

/**
 * Turn a date value into local midnight on that calendar day.
 *
 * Dates are deliberately built with `new Date(year, month, day)`, which is
 * local midnight. The alternative - `new Date("2026-09-01")` - is parsed as
 * UTC, so east of Greenwich it lands on the previous evening and the whole
 * schedule shifts back a day. That off-by-one has already caused real bugs in
 * this project, so every date in DL is built the explicit way.
 *
 * Returns null for anything that is not a usable date.
 */
const toLocalMidnight = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  // "2026-09-01" or "2026-09-01T00:00:00.000Z" - take the calendar part only.
  const parts = String(value).slice(0, 10).split("-");
  if (parts.length !== 3) return null;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return null;

  // Rejects impossible dates such as 2026-02-30, which JavaScript would
  // otherwise roll forward into March without complaint.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

/**
 * Every collection date for a Daily Loan.
 *
 * `startDate` is the FIRST collection - day 1, not the disbursement date. The
 * caller decides when collection begins; this only counts forward from it.
 *
 * Collections run on consecutive calendar days and **Sundays are included**.
 * There is no holiday calendar and no weekend skipping: N collections take
 * exactly N days.
 *
 * Returns an array of `count` Date objects at local midnight, one day apart.
 * Returns an empty array if the start date or count is unusable, so a caller
 * that forgets to check gets no schedule rather than a wrong one.
 */
export const getDlScheduleDates = (
  startDate,
  count = DL_DEFAULT_INSTALMENTS
) => {
  const start = toLocalMidnight(startDate);
  if (!start) return [];

  const total = Math.floor(Number(count));
  if (!Number.isFinite(total) || total <= 0) return [];

  const dates = [];
  for (let i = 0; i < total; i += 1) {
    // Step by calendar day, not by adding 86,400,000 milliseconds. Adding
    // milliseconds drifts across a daylight-saving boundary; setDate does not.
    // India has no daylight saving today, but the schedule should not depend on
    // that staying true.
    dates.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }

  return dates;
};

/**
 * The terms a Daily Loan may be written for, in days.
 *
 * Interest is pro-rated, so each term costs a different amount. Nothing may
 * hardcode 100: the dropdown, the schedule generator and the reports all read
 * this list.
 */
export const DL_TENURES = [100, 115, 120, 125];

/** Days used to pro-rate the annual rate. */
const DL_DAYS_IN_YEAR = 365;

/**
 * Total interest on a Daily Loan.
 *
 * The rate is ANNUAL, and the customer is charged only for the days they
 * actually hold the loan:
 *
 *   interest = principal x (rate / 100) x (days / 365)
 *
 * Rs.10,000 at 36% over 100 days is Rs.986, and the customer repays Rs.10,986.
 *
 * THIS REPLACED A FLAT RULE, AND THE DIFFERENCE IS LARGE
 *
 * Until 2 September 2026 this function charged a flat percentage of the
 * principal - the same Rs.10,000 loan cost Rs.3,600, which is Rs.2,614 too
 * much. The client corrected it. Do not reinstate the flat rule.
 *
 * There is still no `/ 12` anywhere in DL, and there must never be one: that is
 * the monthly divisor, and applying it per day charges a month of interest
 * every day. The divisor here is 365, and it is applied to days, not months.
 *
 * The rate comes from `dlRateOfInterest` on the funding agency.
 */
export const calculateDlInterest = (
  principal,
  ratePercent,
  days = DL_DEFAULT_INSTALMENTS
) => {
  const amount = Number(principal);
  const rate = Number(ratePercent);
  const term = Math.floor(Number(days));

  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  if (!Number.isFinite(term) || term <= 0) return 0;

  return Math.round((amount * (rate / 100) * term) / DL_DAYS_IN_YEAR);
};

/**
 * Everything the customer owes and exactly how it is split across the days.
 *
 * Returns:
 *   totalInterest      interest for the whole term
 *   totalRepayable     principal + interest
 *   higherInstalment   the amount collected on the first `higherDays` days
 *   higherDays         how many days pay the higher amount
 *   lowerInstalment    the amount collected on every remaining day
 *   lowerDays          how many days pay the lower amount
 *   dailyInstalment    alias of higherInstalment, the headline daily figure
 *
 * WHY THERE ARE TWO AMOUNTS
 *
 * The daily figure must be whole rupees - the officer collects cash at a market
 * stall and cannot ask for Rs.93.20. Dividing rarely comes out even, so the
 * remainder has to go somewhere.
 *
 * Putting it all on the final day is wrong. Rs.11,184 over 120 days is Rs.93.20;
 * paying Rs.93 for 119 days leaves Rs.117 due on the last day, far more than
 * every other payment. Rounding up instead gives Rs.94 x 119 = Rs.11,186, which
 * is more than is owed, so the final day comes out at MINUS Rs.2.
 *
 * So the remainder is spread instead. `base` is the whole-rupee floor, and the
 * first `total - base * days` days pay one rupee more. No two payments differ by
 * more than a rupee, the amount only ever steps DOWN, and the total is exact.
 *
 * Rs.10,000 at 36% over 120 days: 24 days at Rs.94, then 96 days at Rs.93.
 */
export const getDlRepaymentTerms = (
  principal,
  ratePercent,
  count = DL_DEFAULT_INSTALMENTS
) => {
  const amount = Number(principal);
  const total = Math.floor(Number(count));

  const empty = {
    principal: 0,
    totalInterest: 0,
    totalRepayable: 0,
    higherInstalment: 0,
    higherDays: 0,
    lowerInstalment: 0,
    lowerDays: 0,
    dailyInstalment: 0,
    instalments: 0,
  };

  if (!Number.isFinite(amount) || amount <= 0) return empty;
  if (!Number.isFinite(total) || total <= 0) return empty;

  const totalInterest = calculateDlInterest(amount, ratePercent, total);
  const totalRepayable = Math.round(amount) + totalInterest;

  const lowerInstalment = Math.floor(totalRepayable / total);
  const higherDays = totalRepayable - lowerInstalment * total;
  const higherInstalment = lowerInstalment + 1;
  const lowerDays = total - higherDays;

  return {
    principal: Math.round(amount),
    totalInterest,
    totalRepayable,
    higherInstalment,
    higherDays,
    lowerInstalment,
    lowerDays,
    // The headline figure quoted to the customer. When the division is exact,
    // higherDays is 0 and every day pays lowerInstalment, so quote that instead.
    dailyInstalment: higherDays > 0 ? higherInstalment : lowerInstalment,
    instalments: total,
  };
};

/**
 * What the customer pays on a given day, counting from 0.
 *
 * Use this when building a schedule, so no caller has to re-derive which days
 * carry the extra rupee.
 */
export const getDlPaymentForDay = (dayIndex, terms) => {
  if (!terms || !Number.isFinite(Number(dayIndex))) return 0;
  const i = Math.floor(Number(dayIndex));
  if (i < 0 || i >= terms.instalments) return 0;
  return i < terms.higherDays ? terms.higherInstalment : terms.lowerInstalment;
};

/**
 * The headline daily amount quoted to the customer.
 *
 * A few days near the end may be one rupee lower - use `getDlRepaymentTerms`
 * when that matters, such as building a schedule or quoting a settlement.
 */
export const calculateDlDailyInstalment = (
  principal,
  ratePercent,
  count = DL_DEFAULT_INSTALMENTS
) => getDlRepaymentTerms(principal, ratePercent, count).dailyInstalment;

/**
 * The DL charge sheet.
 *
 * Every charge is a FLAT PERCENTAGE of the sanctioned amount, taken from the
 * `dl*` columns on the funding agency. Nothing here scales with the tenure.
 *
 * WHY NOT REUSE THE MONTHLY RULES
 *
 * The monthly products define two of these charges in ways that are meaningless
 * over 100 days:
 *
 *   - Security deposit = one month's EMI. A daily loan has no monthly EMI; the
 *     nearest equivalent is one day's collection, which on Rs.10,000 is Rs.122
 *     and is no deposit at all.
 *   - Insurance = rate x (tenure / 24), where tenure is in months. DL stores 100
 *     in `tenureInMonths` because that column really means "number of
 *     instalments". Feed 100 into that formula and the customer is charged for
 *     a 100-month policy - roughly four times the premium of a two-year loan,
 *     on a loan that lasts fourteen weeks.
 *
 * So both are flat percentages here, by decision, not by oversight.
 *
 * The GST is charged on the processing fee alone, matching every other product.
 *
 * Returns the four charges plus `amountToRelease`, the cash the customer
 * actually receives. Each charge has a matching `isPaid` flag: a charge settled
 * in cash at the counter is still charged but is not deducted from the release
 * amount, exactly as the monthly products behave.
 */
export const computeDlCharges = ({
  sanctionedAmount = 0,
  agency = null,
  isProcessingFeePaid = false,
  isGstPaid = false,
  isSecurityDepositPaid = false,
  isInsurancePaid = false,
} = {}) => {
  const principal = Number(sanctionedAmount) || 0;

  const empty = {
    sanctionedAmount: 0,
    processingFee: 0,
    gst: 0,
    securityDeposit: 0,
    insurance: 0,
    totalCharges: 0,
    totalDeducted: 0,
    amountToRelease: 0,
  };

  if (!Number.isFinite(principal) || principal <= 0) return empty;

  // A percentage only applies when its "enabled" flag is on. An agency that has
  // not been configured for DL yet charges nothing, rather than silently
  // borrowing another product's rates - being costed as a GP Loan is a known
  // hazard in this codebase and must not happen quietly.
  const rate = (enabledKey, percentageKey) => {
    if (!agency) return 0;
    if (!agency[enabledKey]) return 0;
    const value = Number(agency[percentageKey]);
    return Number.isFinite(value) && value > 0 ? value : 0;
  };

  const processingFee = Math.round(
    principal * (rate("dlProcessingFeeEnabled", "dlProcessingFeePercentage") / 100)
  );

  // GST is on the processing fee alone, never on the insurance or the deposit.
  const gst = Math.round(
    processingFee * (rate("dlGstEnabled", "dlGstPercentage") / 100)
  );

  const securityDeposit = Math.round(
    principal * (rate("dlSecurityDepositEnabled", "dlSecurityDepositPercentage") / 100)
  );

  const insurance = Math.round(
    principal * (rate("dlInsuranceEnabled", "dlInsurancePercentage") / 100)
  );

  const totalCharges = processingFee + gst + securityDeposit + insurance;

  // Only the charges NOT settled in cash come off the disbursement.
  const totalDeducted =
    (isProcessingFeePaid ? 0 : processingFee) +
    (isGstPaid ? 0 : gst) +
    (isSecurityDepositPaid ? 0 : securityDeposit) +
    (isInsurancePaid ? 0 : insurance);

  // Charges can in principle exceed a very small loan; never hand back a
  // negative amount.
  const amountToRelease = Math.max(0, principal - totalDeducted);

  return {
    sanctionedAmount: Math.round(principal),
    processingFee,
    gst,
    securityDeposit,
    insurance,
    totalCharges,
    totalDeducted,
    amountToRelease,
  };
};

/** "YYYY-MM-DD" for a local date, matching the private helper in utils.js. */
const toYmd = (date) => {
  const d = toLocalMidnight(date);
  if (!d) return null;
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

/** Money off a receipt, which may arrive as "1,234". */
const readAmount = (value) =>
  parseFloat(String(value).replace(/,/g, "")) || 0;

/** The DL collection approval row, if the association was loaded. */
const getDlApproval = (receipt) =>
  receipt?.fk_receipts_hasOne_bl_collection_approval_receiptId ||
  receipt?.dlCollectionApproval ||
  null;

/**
 * Everything a report needs to know about one disbursed Daily Loan: what is
 * still owed, how much of that is overdue, and for how long.
 *
 * This is the DL counterpart of `generateLoanSummaryForMember` in utils.js. It
 * takes the same three arguments and returns the same key names, so a DL row
 * drops into the existing report tables without changing them. A "Daily Loan"
 * row falls through the loanType switch in the monthly function and returns
 * null, which is why this exists.
 *
 * Returns null - not a zeroed row - for anything that is not a usable disbursed
 * DL loan, exactly as the monthly function does, so a caller that forgets to
 * filter shows no row rather than a wrong one.
 *
 * THREE PLACES THIS DELIBERATELY DIFFERS FROM THE MONTHLY ENGINE
 *
 * 1. Interest accrues evenly across the days. DL interest is fixed at
 *    origination (principal x rate/100 x days/365), not recomputed on a falling
 *    balance, so each scheduled day accrues totalInterest / instalments and each
 *    collection pays the accrued-but-unpaid interest first, then principal. On
 *    Rs.10,000 at 36% over 100 days that is Rs.9.86 of interest and about Rs.100
 *    of principal a day, and both reach exactly zero on the final day.
 *
 *    The accrual is held as a float and rounded only at emit. Rounding Rs.9.86
 *    up to Rs.10 on each of 100 days would overstate interest by Rs.14 and leave
 *    a phantom principal balance at closure.
 *
 * 2. There is NO Rs.10 DPD tolerance. The monthly engine forgives a shortfall of
 *    Rs.10 or less (utils.js:2554) to absorb rounding drift over 24 months. DL
 *    needs none: the two-tier split makes every scheduled amount a whole rupee,
 *    so cumulative expected is exact on every day and there is nothing to
 *    absorb. Keeping the tolerance would actively hide missed collections - a
 *    Rs.900 loan over 125 days collects Rs.8 a day, so a whole missed day would
 *    fall under Rs.10 and never show as overdue. The confirmed rule is that one
 *    missed collection puts the loan into DPD immediately.
 *
 * 3. Off-schedule payments are allocated like any other. BL and GPL apply a
 *    receipt that matches no scheduled date wholly to principal and charge no
 *    interest on it (utils.js:2525-2544), which is logged as a defect in
 *    gpl-issues.md; JLG drops such receipts entirely. Over 100 daily collections
 *    paying on the wrong day is ordinary, not an edge case. So this function
 *    walks ALL valid receipts in collected-date order and allocates each against
 *    the interest accrued as at its own date. There is no "unmatched" case.
 *
 * @param {object} data     one flattened member_details row with receiptsDetails
 * @param {string} fromDate unused; accepted so the signature matches the monthly
 *                          function, whose callers all pass "2000-01-01"
 * @param {string} toDate   the "as at" date, "YYYY-MM-DD"
 * @param {object} agency   optional funding agency, for the four `dl*` charge
 *                          rates. Only consulted when the loan has no stored
 *                          charge figures of its own
 */
export const generateDlLoanSummary = (data, fromDate, toDate, agency = null) => {
  if (!data) return null;
  if (data.loanType !== "Daily Loan") return null;
  if (
    data.branchManagerStatus !== "disbursed" ||
    !data.branchManagerStatusUpdatedAt
  ) {
    return null;
  }

  const principal = Math.round(Number(getDisbursementAmount(data)) || 0);
  const rateOfInterest = Number(data.proposedLoanDetails?.rateOfInterest);
  // `tenureInMonths` holds the NUMBER OF INSTALMENTS for DL - 100, 115, 120 or
  // 125 days. It is never months here, whatever the column is called.
  const instalments = Math.floor(Number(data.proposedLoanDetails?.tenureInMonths));

  if (!Number.isFinite(principal) || principal <= 0) return null;
  if (!Number.isFinite(rateOfInterest) || rateOfInterest <= 0) return null;
  if (!Number.isFinite(instalments) || instalments <= 0) return null;

  const terms = getDlRepaymentTerms(principal, rateOfInterest, instalments);
  if (terms.totalRepayable <= 0) return null;

  const startDate =
    data.installmentStartDate ||
    data.emiDateByBranchManager ||
    data.branchManagerStatusUpdatedAt;
  const scheduleDates = getDlScheduleDates(startDate, instalments);
  if (scheduleDates.length === 0) return null;

  // --- Is the loan closed, and was it closed by the report date? ------------
  // Same rule as utils.js:2341-2357: a permanently closed loan is only treated
  // as closed as at a report date on or after its close date, so a historical
  // report still shows the balance that was outstanding then.
  const rawCloseDate = data.loanCloseDate ? new Date(data.loanCloseDate) : null;
  const loanStatus = String(data.loanStatus || "").toLowerCase();
  const isPermanentlyClosed =
    data.status === "Closed" ||
    loanStatus === "foreclosed" ||
    loanStatus === "completed";

  let isClosed = false;
  if (isPermanentlyClosed) {
    if (!toDate) {
      isClosed = true;
    } else if (rawCloseDate && !isNaN(rawCloseDate.getTime())) {
      isClosed = toYmd(toDate) >= toYmd(rawCloseDate);
    } else {
      isClosed = true;
    }
  }

  // --- The date we are reporting as at -------------------------------------
  const benchmarkDate =
    toDate && !isNaN(new Date(toDate).getTime()) ? new Date(toDate) : new Date();
  const today = new Date();
  const comparisonDate = new Date(
    benchmarkDate.getFullYear(),
    benchmarkDate.getMonth(),
    benchmarkDate.getDate(),
    23,
    59,
    59
  );
  // Never look past today, even when the report's To date is in the future -
  // a loan cannot be overdue for days that have not happened yet.
  const overdueComparisonDate = comparisonDate > today ? today : comparisonDate;

  // --- Receipts -------------------------------------------------------------
  // Money collected AFTER the report date must not count. Without this an "as
  // at 31 March" report would spend April's collections to cover March's
  // arrears, showing a loan as Clear when it was actually behind on the day
  // being reported. The monthly engine in utils.js does not filter this way
  // (utils.js:2437) - it only escapes the bug because reports are normally run
  // as at today, when no receipt can be in the future.
  const receipts = (data.receiptsDetails || [])
    .filter((r) => isValidPaidReceiptWithApproval(r, getDlApproval(r)))
    .map((r) => ({
      raw: r,
      amount: readAmount(r.receivedAmount),
      date: toLocalMidnight(r.collectedDate || r.emiDate),
    }))
    .filter((r) => r.amount > 0 && r.date && r.date <= overdueComparisonDate)
    .sort((a, b) => a.date - b.date);

  const totalEmiPaid = Math.round(
    receipts.reduce((sum, r) => sum + r.amount, 0)
  );

  // --- Allocate every payment: accrued interest first, then principal -------
  const dailyAccrual = terms.totalInterest / instalments;

  // How many scheduled days have elapsed by a given date. Interest accrues per
  // scheduled day, so this is what drives how much interest is due.
  const daysElapsedBy = (date) => {
    if (!date) return 0;
    let elapsed = 0;
    for (let i = 0; i < scheduleDates.length; i += 1) {
      if (scheduleDates[i] <= date) elapsed += 1;
      else break;
    }
    return elapsed;
  };

  let outstandingPrincipal = principal;
  let outstandingInterest = terms.totalInterest;
  let principalPaid = 0;
  let interestPaid = 0;

  receipts.forEach((receipt) => {
    const accrued = Math.min(
      terms.totalInterest,
      dailyAccrual * daysElapsedBy(receipt.date)
    );

    // Interest first, but only what has actually accrued by this date. A
    // customer who pays early has not yet been charged the whole term's
    // interest, so the extra money reduces principal.
    const interestDue = Math.max(0, accrued - interestPaid);
    const toInterest = Math.min(interestDue, receipt.amount);
    interestPaid += toInterest;
    outstandingInterest = Math.max(0, outstandingInterest - toInterest);

    const toPrincipal = Math.min(outstandingPrincipal, receipt.amount - toInterest);
    principalPaid += toPrincipal;
    outstandingPrincipal = Math.max(0, outstandingPrincipal - toPrincipal);
  });

  // --- Walk the schedule for DPD and instalments covered --------------------
  // `cumulativeExpected` counts only the days that have fallen due by the report
  // date and drives DPD. `runningTotal` counts every day in the term and is what
  // the schedule table shows, so an upcoming row still displays its own running
  // balance.
  let cumulativeExpected = 0;
  let runningTotal = 0;
  let firstOverdueDate = null;
  let instalmentsCovered = 0;
  const schedule = [];

  scheduleDates.forEach((date, index) => {
    const expected = getDlPaymentForDay(index, terms);
    const dueByNow = date <= overdueComparisonDate;
    runningTotal += expected;

    if (dueByNow) {
      cumulativeExpected += expected;
      // Zero tolerance - see note 2 in the header.
      if (!firstOverdueDate && totalEmiPaid < cumulativeExpected) {
        firstOverdueDate = date;
      }
    }

    // A day counts as collected once the money received covers the schedule up
    // to and including it. Deriving this from cumulative money rather than from
    // date matching means a customer who paid on the wrong day is still
    // credited with the instalment.
    const covered = totalEmiPaid >= runningTotal;
    if (dueByNow && covered) instalmentsCovered += 1;

    schedule.push({
      day: index + 1,
      emiDate: toYmd(date),
      expectedAmount: expected,
      cumulativeExpected: runningTotal,
      status: !dueByNow ? "Upcoming" : covered ? "Paid" : "Unpaid",
    });
  });

  const netShortfall = Math.max(0, cumulativeExpected - totalEmiPaid);

  let overdueDays = 0;
  if (netShortfall > 0 && firstOverdueDate) {
    overdueDays = Math.max(
      0,
      Math.floor((overdueComparisonDate - firstOverdueDate) / 86400000)
    );
  } else {
    firstOverdueDate = null;
  }

  // Split the shortfall the same way the monthly engine does (utils.js:2556-2570):
  // whatever interest should have accrued but has not been paid is overdue
  // interest, and the rest of the gap is overdue principal.
  const accruedToDate = Math.min(
    terms.totalInterest,
    dailyAccrual * daysElapsedBy(overdueComparisonDate)
  );
  const interestShortfall = Math.max(
    0,
    Math.round(accruedToDate) - Math.round(interestPaid)
  );
  const overdueInterest = Math.min(netShortfall, interestShortfall);
  const overduePrincipal = netShortfall - overdueInterest;

  let bucket = "0";
  if (overdueDays > 0 && overdueDays <= 30) bucket = "0-30";
  else if (overdueDays > 30 && overdueDays <= 60) bucket = "31-60";
  else if (overdueDays > 60 && overdueDays <= 90) bucket = "61-90";
  else if (overdueDays > 90 && overdueDays <= 180) bucket = "91-180";
  else if (overdueDays > 180 && overdueDays <= 365) bucket = "181-365";
  else if (overdueDays > 365) bucket = ">365";

  // --- Charges --------------------------------------------------------------
  // Prefer what was actually charged and stored on the loan, as the GP Loan
  // branch does (utils.js:2790-2793). Recomputing would silently restate a
  // historical loan if an agency's rates were edited afterwards.
  // The agency is not carried on the loan row - screens hold it separately as
  // `fundingAgencyData` and pass it in. Fall back to a copy on the row in case a
  // report controller joins it.
  const computed = computeDlCharges({
    sanctionedAmount: principal,
    agency: agency || data.fundingAgencyData || data.fundingAgency || null,
  });
  const pick = (stored, fallback) =>
    stored !== undefined && stored !== null && stored !== ""
      ? formatAmount(stored)
      : fallback;

  const lastReceipt = receipts.length > 0 ? receipts[receipts.length - 1] : null;

  return {
    ...data,
    totalOutstandingPrincipal: isClosed ? 0 : Math.max(0, Math.round(outstandingPrincipal)),
    totalOutstandingInterest: isClosed ? 0 : Math.max(0, Math.round(outstandingInterest)),
    principalPaid: Math.max(0, Math.round(principalPaid)),
    interestPaid: Math.max(0, Math.round(interestPaid)),
    totalEmiPaid: Math.max(0, totalEmiPaid),
    // DL collects daily, so this is the number of DAILY collections covered.
    // `emiDaysPaid` is the name DL screens and DL reports must use - a DL column
    // header must never say "Months".
    emiDaysPaid: instalmentsCovered,
    // Deprecated back-compat alias, same value. It exists only so a DL row
    // rendered by one of the shared monthly reports (MasterDataReport.js:193
    // reads `emiMonthsPaid`) shows the count rather than a blank. Nothing new
    // should read it, and it can be deleted once DL has its own reports.
    emiMonthsPaid: instalmentsCovered,
    insuranceAmount: pick(data.insuranceAmount, computed.insurance),
    processingCharge: pick(data.processingCharge, computed.processingFee),
    gst: pick(data.gst, computed.gst),
    securityDeposit: pick(data.securityDeposit, computed.securityDeposit),
    totalPendingAmount: isClosed ? 0 : netShortfall,
    status: isClosed
      ? loanStatus === "foreclosed"
        ? "Foreclosed"
        : "Completed"
      : netShortfall > 0
      ? "Pending"
      : "Clear",
    // These two must never diverge: ParReport filters on `dpd` (:76) but
    // displays `overdueDays` (:153), so a mismatch shows loans with a blank age.
    overdueDays: isClosed ? 0 : overdueDays,
    dpd: isClosed ? 0 : overdueDays,
    bucket: isClosed ? "0" : bucket,
    overduePrincipal: isClosed ? 0 : Math.max(0, Math.round(overduePrincipal)),
    overdueInterest: isClosed ? 0 : Math.max(0, Math.round(overdueInterest)),
    totalPAR: isClosed
      ? 0
      : overdueDays > 0
      ? Math.max(0, Math.round(outstandingPrincipal))
      : 0,
    lastPaidDate: lastReceipt ? toYmd(lastReceipt.date) : null,
    firstOverdueDate: isClosed ? null : toYmd(firstOverdueDate),
    // The headline daily collection. A few days near the end may be a rupee
    // lower - `schedule` carries the exact amount for every day.
    emiAmount: terms.dailyInstalment,
    totalLoanInterest: terms.totalInterest,
    firstEmiDate: toYmd(scheduleDates[0]),
    lastEmiDate: toYmd(scheduleDates[scheduleDates.length - 1]),
    isSettled: isClosed,
    schedule,
  };
};
